import {
    PageAPI
} from 'lib/api';
import {
    dateFormat,
    apiDateFormatter
} from 'lib/format';
import {
    apiEnvSelector,
    isDomesticSelector
} from 'common/selectors/appSelector';
import { collectionParamsSelector } from 'common/selectors/apiParamsSelector';
import {
    graphql
} from 'common/actions/apiAction';
import StatusFilter from '../StatusFilter';

export const START_GET_SCHEDULE = 'START_GET_SCHEDULE';
export const COMPLETE_GET_SCHEDULE = 'COMPLETE_GET_SCHEDULE';
export const START_GET_CATEGORIES = 'START_GET_CATEGORIES';
export const COMPLETE_GET_CATEGORIES = 'COMPLETE_GET_CATEGORIES';
export const SET_STATUS_FILTER = 'SET_STATUS_FILTER';
export const SET_SPORTS_FILTER = 'SET_SPORTS_FILTER';
export const SET_NETWORK_FILTER = 'SET_NETWORK_FILTER';
export const SET_DATE_FILTER = 'SET_DATE_FILTER';
export const SET_REPLAY_DATE_FILTER = 'SET_REPLAY_DATE_FILTER';

export function startGetSchedule() {
    return {
        type: START_GET_SCHEDULE,
        startTime: Date.now()
    };
}

export function completeGetSchedule(listings) {
    return {
        type: COMPLETE_GET_SCHEDULE,
        completeTime: Date.now(),
        listings
    };
}

export function setStatusFilter(status) {
    return {
        type: SET_STATUS_FILTER,
        status
    };
}

export function getQueryFromParams({
    type = StatusFilter.Options.LIVE,
    categoryId,
    channel,
    startDate,
    endDate
}) {
    const categories = categoryId && [categoryId.toString()];

    const packageMap = {
        espn_plus: 'espn_plus',
        espn_plus_mlb: 'espn_plus_mlb',
        espn_plus_nhl: 'espn_plus_nhl'
    };

    let packages = null;
    let networks = null;

    // ESPN+, MLB.TV, and NHL.TV are treated as packages, and not networks
    if (channel && packageMap[channel.toLowerCase()]) {
        packages = [channel.toLowerCase()];
    } else {
        networks = channel && [channel.toLowerCase()];
    }

    let dates = [startDate];

    // end date is only used for replays, and should always reflect current time of today
    const end = new Date();

    if (endDate) {
        dates.push(end);
    }
    if (type === StatusFilter.Options.REPLAY && !startDate) { // set default date range
        const start = new Date();
        dates = [apiDateFormatter(start)];
    }

    return {
        type: type.toUpperCase(),
        networks,
        packages,
        categories,
        dates
    };
}

export function fetchSchedule(params) {
    return (dispatch, getState) => {
        dispatch(startGetSchedule());
        const state = getState();
        const isDomestic = isDomesticSelector(state);

        const {
            type: typeArg,
            categories,
            networks,
            packages,
            dates
        } = getQueryFromParams(params);
        let day;
        let start;
        let end;
        if (dates.length === 1) {
            day = dateFormat.paramToCalendarDatesString(dates[0]);
        } else if (dates.length === 2) {
            // API only allows the date to be down to minute precision:(
            start = dates && dates[0] && dateFormat.getISOStringWithMinPrecision(dateFormat.paramToDate(dates[0]));
            end = dates && dates[1] && dateFormat.getISOStringWithMinPrecision(dates[1]);
        }
        const limit = 500;

        return dispatch(graphql('getAirings', {
            type: typeArg,
            categories,
            networks,
            packages,
            start,
            end,
            day,
            limit
        }))
          .then(({
              data = {}
          }) => {
              let {
                  airings
              } = data;
              airings = airings || []; // response could return `null` for `airings` node when there is an error
              const listings = airings.map(({
                id,
                name,
                type,
                startDateTime,
                shortDate,
                mediumTime,
                network,
                category,
                subcategory,
                feedName,
                purchaseImage: {
                    url: backgroundImageHref
                },
                image: {
                    url: imageUrl
                } = {},
                packages: packageArray
                }) => {
                  const mapped = {
                      id,
                      name,
                      feedName,
                      type: type.toLowerCase(),
                      startTime: startDateTime,
                      startDate: shortDate,
                      mediumTime,
                      backgroundImageHref,
                      broadcasts: network ? [{
                          abbreviation: isDomestic ? network.abbreviation.toLowerCase() : network.adobeResource,
                          adobeResource: network.adobeResource,
                          name: network.name,
                          code: network.id.toUpperCase(),
                          type: network.type,
                          shortName: network.shortName,
                          ipAuth: network.isIpAuth,
                          packages: packageArray ? packageArray.map(({
                                name: nameArg
                            }) => nameArg) : []
                      }] : [{}],
                      thumbnails: {
                          large: {
                              width: 640,
                              height: 360,
                              href: imageUrl
                          }
                      },
                      categories: [category],
                      subcategories: subcategory ? [subcategory] : []
                  };
                  return mapped;
              });

              dispatch(completeGetSchedule(listings));

              return listings;
          });
    };
}
export function fetchSyndicatedSchedule(params) {
    return (dispatch, getState) => {
        dispatch(startGetSchedule());
        const state = getState();
        const isDomestic = isDomesticSelector(state);

        const {
            type: typeArg,
            categories,
            networks,
            packages
        } = getQueryFromParams(params);
        const dates = params.dates || apiDateFormatter(new Date());

        let day;
        let start;
        let end;

        if (params.dates && params.dates.match(/-/)) {
            [start, end] = params.dates.split('-');
            // API only allows the date to be down to minute precision:(
            start = dateFormat.getISOStringWithMinPrecision(dateFormat.paramToDate(start));
            end = dateFormat.getISOStringWithMinPrecision(dateFormat.paramToDate(end));
        } else {
            day = dateFormat.paramToCalendarDatesString(dates);
        }
        const limit = 500;

        return dispatch(graphql('getAirings', {
            type: typeArg,
            categories,
            networks,
            packages,
            start,
            end,
            day,
            limit
        }))
          .then(({
              data = {}
          }) => {
              let {
                  airings
              } = data;
              airings = airings || []; // response could return `null` for `airings` node when there is an error
              // TODO: update schedule page to reflect the data structure from GraphQL API so that this mapping is not necessary
              const listings = airings.map(({
                id,
                name,
                type,
                startDateTime,
                shortDate,
                mediumTime,
                network,
                category,
                subcategory,
                feedName,
                purchaseImage: {
                    url: backgroundImageHref
                } = {},
                image: {
                    url: imageUrl
                } = {},
                packages: packageArray
                }) => {
                  const mapped = {
                      id,
                      name,
                      feedName,
                      type: type.toLowerCase(),
                      startTime: startDateTime,
                      startDate: shortDate,
                      mediumTime,
                      backgroundImageHref,
                      broadcasts: network ? [{
                          abbreviation: isDomestic ? network.abbreviation.toLowerCase() : network.adobeResource,
                          adobeResource: network.adobeResource,
                          name: network.name,
                          code: network.id.toUpperCase(),
                          type: network.type,
                          shortName: network.shortName,
                          ipAuth: network.isIpAuth,
                          packages: packageArray ? packageArray.map(({
                                name: nameArg
                            }) => nameArg) : []
                      }] : [{}],
                      thumbnails: {
                          large: {
                              width: 640,
                              height: 360,
                              href: imageUrl
                          }
                      },
                      categories: [category],
                      subcategories: subcategory ? [subcategory] : []
                  };

                  return mapped;
              });

              dispatch(completeGetSchedule(listings));

              return listings;
          });
    };
}

export function startGetCategories() {
    return {
        type: START_GET_CATEGORIES,
        startTime: Date.now()
    };
}

export function completeGetCategories(categories) {
    return {
        type: COMPLETE_GET_CATEGORIES,
        completeTime: Date.now(),
        categories
    };
}

export function fetchCategories() {
    return (dispatch, getState) => {
        const state = getState();
        const apiEnv = apiEnvSelector(state);
        const params = collectionParamsSelector(state);
        dispatch(startGetCategories);
        return PageAPI.getCategories(apiEnv, params).then((categories) => {
            dispatch(completeGetCategories(categories));
            return categories;
        });
    };
}

export function setSportsFilter(option) {
    return {
        type: SET_SPORTS_FILTER,
        value: option.value,
        filterType: option.type,
        label: option.label
    };
}

export function setNetworkFilter(option) {
    return {
        type: SET_NETWORK_FILTER,
        value: option.value,
        label: option.label
    };
}

export function setDateFilter(date) {
    return {
        type: SET_DATE_FILTER,
        date
    };
}

export function setReplayDateFilter(startDate, endDate) {
    return {
        type: SET_REPLAY_DATE_FILTER,
        startDate,
        endDate
    };
}



// WEBPACK FOOTER //
// ./app/common/schedule/actions/index.js